import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listings, Listing } from '../api/client';
import { getAmenityLabel } from '../data/amenities';
import { formatPrice } from '../utils/format';

const COMPARE_STORAGE_KEY = 'darlink_compare_ids';
const MAX_COMPARE = 3;

export function getCompareIds(): string[] {
  try {
    const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((id) => typeof id === 'string').slice(0, MAX_COMPARE) : [];
  } catch {
    return [];
  }
}

export function addToCompare(listingId: string): void {
  const ids = getCompareIds();
  if (ids.includes(listingId) || ids.length >= MAX_COMPARE) return;
  localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify([...ids, listingId]));
  window.dispatchEvent(new Event('storage'));
}

export function removeFromCompare(listingId: string): void {
  const ids = getCompareIds().filter((id) => id !== listingId);
  localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(ids));
  window.dispatchEvent(new Event('storage'));
}

export default function Compare() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const [ids, setIds] = useState<string[]>(getCompareIds);
  const [items, setItems] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handler = () => setIds(getCompareIds());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  useEffect(() => {
    if (ids.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(ids.map((id) => listings.get(id).catch(() => null)))
      .then((results) => setItems(results.filter((l): l is Listing => l != null)))
      .finally(() => setLoading(false));
  }, [ids.join(',')]);

  const remove = (listingId: string) => {
    removeFromCompare(listingId);
    setIds(getCompareIds());
  };

  if (ids.length === 0 && !loading) {
    return (
      <div style={{ maxWidth: 800, margin: '2rem auto', padding: '2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{t('compare.title')}</h1>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{t('compare.empty')}</p>
        <Link to="/listings" style={{ color: '#0d9488', fontWeight: 600 }}>{t('compare.browseListings')}</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', margin: 0 }}>{t('compare.title')}</h1>
        <Link to="/listings" style={{ color: '#0d9488', fontWeight: 600, textDecoration: 'none' }}>← {t('common.backToSearch')}</Link>
      </div>
      {loading && <p style={{ color: '#6b7280' }}>{t('common.loading')}</p>}
      {!loading && items.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '0.75rem', minWidth: 100 }}>{t('compare.property')}</th>
                {items.map((l) => (
                  <th key={l.listingId} style={{ textAlign: 'left', padding: '0.75rem', minWidth: 180, verticalAlign: 'top' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Link to={`/listings/${l.listingId}`} style={{ fontWeight: 600, color: '#0d9488', textDecoration: 'none' }}>
                        {(lang === 'ar' && l.titleAr) ? l.titleAr : (l.titleEn || '')}
                      </Link>
                      <button type="button" onClick={() => remove(l.listingId)} style={{ background: 'none', border: 0, cursor: 'pointer', color: '#9ca3af', fontSize: '1.2rem' }} aria-label={t('compare.remove')}>×</button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{t('common.price')}</td>
                {items.map((l) => (
                  <td key={l.listingId} style={{ padding: '0.75rem', fontWeight: 600 }}>{formatPrice(l.price, l.currency)}</td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{t('form.bedrooms')}</td>
                {items.map((l) => (
                  <td key={l.listingId} style={{ padding: '0.75rem' }}>{l.bedrooms ?? '—'}</td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{t('form.bathrooms')}</td>
                {items.map((l) => (
                  <td key={l.listingId} style={{ padding: '0.75rem' }}>{l.bathrooms ?? '—'}</td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{t('common.size')}</td>
                {items.map((l) => (
                  <td key={l.listingId} style={{ padding: '0.75rem' }}>{l.areaSqm != null ? `${l.areaSqm} m²` : '—'}</td>
                ))}
              </tr>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '0.75rem', color: '#6b7280' }}>{t('common.amenities')}</td>
                {items.map((l) => (
                  <td key={l.listingId} style={{ padding: '0.75rem' }}>
                    {l.amenities && l.amenities.length > 0
                      ? l.amenities.map((k) => getAmenityLabel(k, lang)).join(', ')
                      : '—'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
